import { useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";
import { EXTERIORS, INTERIORS, FLOOR_PLANS, GROUND_SET } from "@/data/site";

const FP_CATEGORIES = ["All", ...new Set(FLOOR_PLANS.map((f) => f.category))];

const ImageCard = ({ item, index, onSelect, testIdPrefix }) => (
  <Reveal delay={(index % 4) * 60}>
    <button
      type="button"
      data-testid={`${testIdPrefix}-${index}`}
      onClick={() => onSelect(item)}
      className="group block w-full text-left overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 focus:outline-none focus:ring-4 focus:ring-amber-500/50"
    >
      <div className="overflow-hidden">
        <img
          src={item.img}
          alt={item.caption}
          loading="lazy"
          className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <p className="px-4 py-3 text-xs font-semibold text-slate-700">{item.caption}</p>
    </button>
  </Reveal>
);

const Gallery = () => {
  const [selected, setSelected] = useState(null);
  const [fpCategory, setFpCategory] = useState("All");

  const filteredPlans = useMemo(
    () => (fpCategory === "All" ? FLOOR_PLANS : FLOOR_PLANS.filter((f) => f.category === fpCategory)),
    [fpCategory]
  );

  return (
    <div data-testid="gallery-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/ext-hardi-1.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">Photo Gallery</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Real buildings. Real projects.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Exterior options are endless — we create the design that perfectly meets your needs and budget while
              meeting code requirements.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-8 pb-14 sm:pt-10 sm:pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Tabs defaultValue="exteriors">
          <TabsList
            data-testid="gallery-tabs"
            className="flex flex-wrap h-auto gap-2 bg-slate-100 p-2 rounded-xl justify-start"
          >
            <TabsTrigger data-testid="gallery-tab-exteriors" value="exteriors" className="px-5 py-2.5 font-bold">
              Sample Exteriors
            </TabsTrigger>
            <TabsTrigger data-testid="gallery-tab-interiors" value="interiors" className="px-5 py-2.5 font-bold">
              Sample Interiors
            </TabsTrigger>
            <TabsTrigger data-testid="gallery-tab-floor-plans" value="floor-plans" className="px-5 py-2.5 font-bold">
              Sample Floor Plans
            </TabsTrigger>
            <TabsTrigger data-testid="gallery-tab-ground-set" value="ground-set" className="px-5 py-2.5 font-bold">
              Ground Set Process
            </TabsTrigger>
          </TabsList>

          <TabsContent value="exteriors" className="mt-10">
            <p className="mb-8 text-sm text-slate-600 max-w-3xl">
              Exterior options are endless. We can create the design that perfectly meets your needs and budget
              while also meeting code requirements.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {EXTERIORS.map((item, i) => (
                <ImageCard key={item.img + i} item={item} index={i} onSelect={setSelected} testIdPrefix="gallery-exterior" />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="interiors" className="mt-10">
            <p className="mb-8 text-sm text-slate-600 max-w-3xl">
              Modular buildings accommodate every interior need including daycares, classrooms, offices, club
              houses, medical facilities, restaurants, retail stores, and more.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {INTERIORS.map((item, i) => (
                <ImageCard key={item.img + i} item={item} index={i} onSelect={setSelected} testIdPrefix="gallery-interior" />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="floor-plans" className="mt-10">
            <p className="mb-6 text-sm text-slate-600 max-w-3xl">
              We work with you to create a unique and customized floor plan to meet your specific needs. Explore
              our floor samples below.
            </p>
            <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label="Filter floor plans by category">
              {FP_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  data-testid={`fp-filter-${cat.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  aria-pressed={fpCategory === cat}
                  onClick={() => setFpCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors duration-150 ${
                    fpCategory === cat
                      ? "bg-[#00203d] text-white border-[#00203d]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#00203d]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPlans.map((item, i) => (
                <ImageCard key={item.img} item={item} index={i} onSelect={setSelected} testIdPrefix="gallery-floor-plan" />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="ground-set" className="mt-10">
            <p className="mb-8 text-sm text-slate-600 max-w-3xl">
              Ground Set buildings are a great option. The process is efficient and seamless with our team of
              expert installers.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {GROUND_SET.map((item, i) => (
                <ImageCard key={item.img + i} item={item} index={i} onSelect={setSelected} testIdPrefix="gallery-ground-set" />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-4xl p-2 sm:p-3">
          <DialogTitle className="sr-only">{selected?.caption || "Gallery image"}</DialogTitle>
          {selected && (
            <figure>
              <img src={selected.img} alt={selected.caption} className="w-full max-h-[75vh] object-contain rounded-lg bg-slate-50" />
              <figcaption className="px-2 py-3 text-sm font-semibold text-slate-700">{selected.caption}</figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
